import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { fs } from "@/lib/firebase";
import type { ParsedQr } from "@/lib/qrParse";
import type { KitchenDayOverride, KitchenHour } from "@/lib/kitchenHours";
import type { AvailabilityHours } from "@/lib/availability";

export interface BuffetSettings {
  maxQuantity?: number;
  maxOrders?: number;
  waitingTime?: number; // minutes
  maxQuantityPerMenu?: number;
  menuCount?: number;
  kitchenHours?: KitchenHour[];
}

export type LanguageCode = "FR" | "EN" | "CN" | "IT" | "ES" | "DE" | "PT" | "JA" | "KO";

export interface ServiceAction {
  id?: string;
  name: string;
  name_trans?: Partial<Record<LanguageCode, string>>;
  enabled?: boolean;
  printerId?: string;
  icon?: string;
}

export interface RestaurantInfo {
  id?: string;
  /** Repli racine — la tablette lit `informations.name`. Utiliser getRestaurantName(). */
  name?: string;
  color?: string;
  image?: { path?: string };
  /** Square brand logo (different from `image.path`, which is a hero/thumbnail). */
  logo?: string;
  /** Repli racine — la tablette lit `settings.canOrder`. Utiliser isOrderingSuspended(). */
  canOrder?: boolean;
  /** Repli racine — la tablette lit `settings.aLaCarte`. Utiliser isALaCarte(). */
  aLaCarte?: boolean;
  kitchenHours?: KitchenHour[];
  buffetSettings?: BuffetSettings;
  informations?: {
    id?: string;
    name?: string;
    introMessage?: string;
  };
  settings?: {
    /** Commandes suspendues quand faux (Cart.js:404). */
    canOrder?: boolean;
    /** À la carte : tous les produits comptent dans le plafond global ; sinon seuls les gratuits. */
    aLaCarte?: boolean;
    autoAcceptOrder?: boolean;
    /** Un serveur doit saisir son code avant l'envoi (Cart.js:713-722). */
    waiterConfirm?: boolean;
    /** Mode consultation sur place quand faux : pas de panier ni d'envoi. */
    showSitInCart?: boolean;
    /** Idem pour un QR à emporter (`takeawayMenu`). */
    showTakeAwayCart?: boolean;
    color?: string;
    languages?: Partial<Record<LanguageCode, boolean>>;
    /** Horaires cuisine par défaut (source de vérité écrite par l'admin). */
    kitchenHours?: KitchenHour[];
    /** Jours personnalisés : "0"…"6" ou "publicHoliday". */
    kitchenHoursOverrides?: Record<string, KitchenDayOverride>;
    /** Plages midi / soir servant au filtre de disponibilité des produits. */
    availabilityHours?: AvailabilityHours | null;
    /** Affichage des catégories choisi dans l'admin. Utiliser getCategoryLayout(). */
    smartphoneCategoryLayout?: string;
  };
  services?: ServiceAction[];
}

/** Live read of the Restaurant root doc — used for header, splash, and submit gate. */
export const useRestaurant = (parsed: ParsedQr | null): RestaurantInfo | null => {
  const [info, setInfo] = useState<RestaurantInfo | null>(null);

  useEffect(() => {
    if (!parsed) return;
    const ref = doc(fs, `Restaurant/${parsed.restoId}`);
    const unsub = onSnapshot(ref, (snap) => {
      setInfo(snap.exists() ? (snap.data() as RestaurantInfo) : null);
    });
    return unsub;
  }, [parsed?.restoId]);

  return info;
};
